import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CreateOrganizationProps, LoadOrganizationProps, ReduxOrganizationAddEditState, UpdateOrganizationProps, LoadUserGroupTestPriceProps } from '../types';
import { ISelectItem, ReduxSliceState } from '../../../shared/types';
import { withPayloadType } from '../../../shared/utils';
import { ReduxRootState } from '../../../shared/redux/store';
import { OrganizationItem, UserGroup, UserGroupTestPrice, AccountRep, AccountRepsResponse, OrderStats, Enterprise  } from '../../../api/types';

const initialState: ReduxOrganizationAddEditState = {
    state: 'BUSY',
    organization: undefined,
    userGroups: [],
    enterprises: [],
    accountReps: [],
    userGroupTestPrices: undefined,
    orderStats: undefined
};

export const createOrganization = createAction('organizations/add-edit/createOrganization', withPayloadType<CreateOrganizationProps>());
export const updateOrganization = createAction('organizations/add-edit/updateOrganization', withPayloadType<UpdateOrganizationProps>());
export const loadOrganization = createAction('organizations/add-edit/loadOrganization', withPayloadType<LoadOrganizationProps>());
export const loadUserGroups = createAction('organizations/add-edit/loadUserGroups');
export const loadEnterprises = createAction('organizations/add-edit/loadEnterprises');
export const loadAccountReps = createAction('organizations/add-edit/loadAccountReps');
export const loadUserGroupTestPrices = createAction('organizations/add-edit/loadUserGroupTestPrices', withPayloadType<LoadUserGroupTestPriceProps>());
export const loadOrderStats = createAction('organizations/add-edit/loadOrderStats', withPayloadType<LoadOrganizationProps>());

const organizationAddEditSlice = createSlice({
    name: 'organizations/add-edit',
    initialState,
    reducers: {
        setState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.state = action.payload;
        },
        setOrganization: (state, action: PayloadAction<OrganizationItem | undefined>) => {
            state.organization = {...action.payload};
        },
        setUserGroups: (state, action: PayloadAction<UserGroup[]>) => {
            state.userGroups = action.payload;
        },
        setEnterprises: (state, action: PayloadAction<Enterprise[]>) => {
            state.enterprises = action.payload;
        },
        setUserGroupTestPrices: (state, action: PayloadAction<UserGroupTestPrice | undefined>) => {
            state.userGroupTestPrices = action.payload;
        },
        setAccountReps: (state, action: PayloadAction<AccountRep[]>) => {
            state.accountReps = action.payload;
        },
        setOrderStats: (state, action: PayloadAction<OrderStats | undefined>) => {
            state.orderStats = action.payload;
        },
    }
});

export default organizationAddEditSlice;
const selectSelf = (state: ReduxRootState) => state.organizationAddEdit;
export const selectOrganizationAddEditState = createSelector(selectSelf, self => self.state);
export const selectOrganizationAddEditIsBusy = createSelector(selectOrganizationAddEditState, state => state === 'BUSY');
export const selectOrganization = createSelector(selectSelf, self => self.organization);
export const selectOrganizationId = createSelector(selectOrganization, org => ({ orgId: org?.orgId, orgName: org?.name }));
export const selectOrganizationAddEditUserGroups = createSelector(selectSelf, self => self.userGroups);
export const selectOrganizationAddEditUserGroupTestPrices = createSelector(selectSelf, self => self.userGroupTestPrices);
export const selectOrganizationAddEditOrderStats = createSelector(selectSelf, self => self.orderStats);
export const selectOrganizationAddEditAccountReps = createSelector(selectSelf, self => self.accountReps);
export const selectOrganizationAddEditAccountOwners = createSelector(selectSelf, self => self.accountReps.filter((rep) => rep.position === 'Account Owner'));
export const selectOrganizationAddEditAccountManagers = createSelector(selectSelf, self => self.accountReps.filter((rep) => rep.position === 'Account Manager'));
export const selectOrganizationAddEditAccountRepOwnersList = createSelector(selectOrganizationAddEditAccountOwners, accountReps => accountReps.map<ISelectItem>(accountRep => ({
    label: accountRep.fullName!,
    value: accountRep.fullName!,
})));
export const selectOrganizationAddEditAccountRepManagersList = createSelector(selectOrganizationAddEditAccountManagers, accountReps => accountReps.map<ISelectItem>(accountRep => ({
    label: accountRep.fullName!,
    value: accountRep.fullName!,
})));
export const selectOrganizationAddEditUserGroupsList = createSelector(
    selectOrganizationAddEditUserGroups,
    userGroups => userGroups.map<ISelectItem>(userGroup => ({
        label: userGroup.name!,
        value: userGroup.userGroupId?.toString(),
    }))
);
export const selectOrganizationAddEditEnterprises = createSelector(selectSelf, self => self.enterprises);
// Leading blank option lets an org be un-mapped from a Enterprise again (FormSelect has no
// separate "clear" affordance) - same reasoning as any other optional-FK dropdown in this form.
export const selectOrganizationAddEditEnterprisesList = createSelector(
    selectOrganizationAddEditEnterprises,
    enterprises => [
        { label: '(None)', value: '' },
        ...enterprises.map<ISelectItem>(enterprise => ({
            label: enterprise.name,
            value: enterprise.enterpriseId?.toString(),
        })),
    ]
);
